import { useDispatch } from "react-redux";
import { Button, Col, DatePicker, Drawer, Form, InputNumber, Row, Select, Space, Tooltip, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { showNotification } from "../../../shared/utils/utils";
import { addForex, updateForexDetails } from "../../../core/store/reducers/master.slice";
import dayjs from "dayjs";

interface Props {
  onClose: () => void;
  visible: boolean;
  selectedEntity?: any;
  onSubmit?: () => void;
  currencies: any;
}

const CreateEditForexComponent = ({ onClose, visible, selectedEntity = null, currencies, onSubmit = () => {} }: Props) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [sampleAmount, setSampleAmount] = useState<number | null>(null);
  const fromCurrencyId = Form.useWatch("fromCurrencyId", form);
  const targets = Form.useWatch("targets", form) || [];
  const fromMonth = Form.useWatch("fromMonth", form);
  const editing = !!selectedEntity;
  const currencyOptions = (currencies || []).map((currency: any) => ({ value: currency.id, label: currency.name }));

  useEffect(() => {
    form.resetFields();
    setSampleAmount(null);
    if (selectedEntity) {
      form.setFieldsValue({
        fromCurrencyId: selectedEntity.fromCurrencyId || selectedEntity.currencyId,
        targets: (selectedEntity.targets || (selectedEntity.toCurrencyIds || [selectedEntity.toCurrencyId]).map((toCurrencyId: string) => ({ toCurrencyId, rate: selectedEntity.rate || selectedEntity.inrMultiplier })))
          .map((target: any) => ({ toCurrencyId: target.toCurrencyId, rate: Number(target.rate) })),
        status: selectedEntity.status || "ACTIVE",
        fromMonth: dayjs(selectedEntity.fromMonth || selectedEntity.startDate),
        toMonth: dayjs(selectedEntity.toMonth || selectedEntity.endDate),
      });
    }
  }, [form, selectedEntity]);

  const onFinish = async (values: any) => {
    if (saving) return;
    const payload = {
      fromCurrencyId: values.fromCurrencyId,
      targets: values.targets.map((target: any) => ({ toCurrencyId: target.toCurrencyId, rate: Number(target.rate) })),
      status: values.status,
      fromMonth: dayjs(values.fromMonth).startOf("month").format("YYYY-MM-DD"),
      toMonth: dayjs(values.toMonth).startOf("month").format("YYYY-MM-DD"),
    };
    setSaving(true);
    try {
      const result: any = await dispatch(editing ? updateForexDetails({ id: selectedEntity.id, body: payload }) : addForex(payload));
      if (result?.payload?.data?.success) {
        showNotification(editing ? "Forex Details Updated Successfully" : "Forex Added Successfully", "success");
        onSubmit();
      } else {
        const message = result?.payload?.data?.message;
        showNotification(typeof message === "string" ? message : "Unable to save Forex details.", "error");
      }
    } catch {
      showNotification("Unable to save Forex details.", "error");
    } finally { setSaving(false); }
  };

  const preview = (index: number) => {
    const target = targets[index];
    const rate = Number(target?.rate);
    const currencyName = currencyOptions.find((currency: any) => currency.value === target?.toCurrencyId)?.label;
    if (sampleAmount === null || !Number.isFinite(sampleAmount) || sampleAmount < 0 || !fromCurrencyId
      || !currencyName || target.toCurrencyId === fromCurrencyId || !Number.isFinite(rate) || rate <= 0) return "-";
    return (sampleAmount * rate).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " " + currencyName;
  };

  return <Drawer maskClosable={false} keyboard={!saving} closable={!saving}
    title={<strong>{editing ? "Edit Forex Details" : "Add New Forex"}</strong>}
    className="form-drawer" width={780} contentWrapperStyle={{ maxWidth: "100vw" }} open={visible} onClose={onClose}
    footer={<Space wrap>
      <Button disabled={saving} onClick={onClose}>Cancel</Button>
      {!editing && <Button disabled={saving} onClick={() => { form.resetFields(); setSampleAmount(null); }}>Clear</Button>}
      <Button type="primary" loading={saving} onClick={() => form.submit()}>Save</Button>
    </Space>}>
    <Form form={form} name="creat-edit-forex" layout="vertical" onFinish={onFinish}
      initialValues={{ status: "ACTIVE", targets: [{}] }} disabled={saving}>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item name="fromCurrencyId" label="From Currency" rules={[{ required: true, message: "Please select From Currency" }]}>
            <Select showSearch optionFilterProp="label" allowClear disabled={editing || saving} options={currencyOptions}
              placeholder="From Currency" onChange={() => form.validateFields(["targets"]).catch(() => {})} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="Sample Amount">
            <InputNumber aria-label="Sample amount" min={0} max={9999999999999.99} precision={2}
              value={sampleAmount} onChange={setSampleAmount} placeholder="Sample Amount" style={{ width: "100%" }} />
          </Form.Item>
        </Col>
      </Row>
      <Form.List name="targets" rules={[{ validator: async (_, values) => {
        if (!values?.length) throw new Error("At least one To Currency is required");
        const ids = values.map((target: any) => target?.toCurrencyId).filter(Boolean);
        if (new Set(ids).size !== ids.length) throw new Error("Each To Currency must be unique");
        if (ids.includes(form.getFieldValue("fromCurrencyId"))) throw new Error("Same-currency rates are always 1 and are not stored");
      } }]}>
        {(fields, { add, remove }, { errors }) => <>
          {fields.map((field) => <Row key={field.key} gutter={12} align="top" style={{ borderBottom: "1px solid #e2e8f0", marginBottom: 16 }}>
            <Col xs={12} sm={8}>
              <Form.Item name={[field.name, "toCurrencyId"]} label="To Currency" rules={[{ required: true, message: "Please select To Currency" }]}>
                <Select aria-label={"To currency " + (field.name + 1)} showSearch optionFilterProp="label" allowClear disabled={editing || saving}
                  placeholder="To Currency" options={currencyOptions.map((option: any) => ({ ...option,
                    disabled: option.value === fromCurrencyId || targets.some((target: any, index: number) => index !== field.name && target?.toCurrencyId === option.value),
                  }))} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item name={[field.name, "rate"]} label="Rate" rules={[{ required: true, message: "Please enter Rate" }, { type: "number", min: 0.000001, message: "Rate must be greater than zero" }]}>
                <InputNumber aria-label={"Rate " + (field.name + 1)} disabled={editing || saving} min={0.000001}
                  max={999999999999} step={0.000001} precision={6}
                  formatter={(value, info) => info.userTyping ? info.input : value === undefined || value === null ? "" : String(Number(value))}
                  placeholder="Rate" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={20} sm={8}>
              <Form.Item label="Converted Amount">
                <Typography.Text strong aria-live="polite" style={{ display: "block", paddingTop: 5, overflowWrap: "anywhere" }}>{preview(field.name)}</Typography.Text>
              </Form.Item>
            </Col>
            {!editing && <Col xs={4} sm={2} style={{ paddingTop: 30 }}>
              <Tooltip title="Remove currency"><Button type="text" danger icon={<DeleteOutlined />} aria-label={"Remove currency " + (field.name + 1)}
                disabled={fields.length === 1 || saving} onClick={() => remove(field.name)} /></Tooltip>
            </Col>}
          </Row>)}
          <Form.ErrorList errors={errors} />
          {!editing && <Button icon={<PlusOutlined />} disabled={saving || fields.length >= currencyOptions.filter((option: any) => option.value !== fromCurrencyId).length}
            onClick={() => add({})} style={{ marginBottom: 24 }}>Add Currency</Button>}
        </>}
      </Form.List>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item name="fromMonth" label="From Month" rules={[{ required: true, message: "Start Month Required" }]}>
            <DatePicker picker="month" disabled={editing || saving} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="toMonth" label="To Month" dependencies={["fromMonth"]} rules={[{ required: true, message: "End Month Required" },
            ({ getFieldValue }) => ({ validator: async (_, value) => {
              if (value && getFieldValue("fromMonth") && value.isBefore(getFieldValue("fromMonth"), "month")) throw new Error("End Month cannot be earlier than Start Month");
              if (editing && value && value.isAfter(dayjs(selectedEntity.toMonth || selectedEntity.endDate), "month")) throw new Error("To Month may only be shortened");
            } }),
          ]}>
            <DatePicker picker="month" style={{ width: "100%" }} disabledDate={(date) =>
              !!(fromMonth && date.isBefore(fromMonth, "month")) || !!(editing && date.isAfter(dayjs(selectedEntity.toMonth || selectedEntity.endDate), "month"))} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select options={[{ value: "ACTIVE", label: "Active" }, { value: "INACTIVE", label: "Inactive" }]} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  </Drawer>;
};
export default CreateEditForexComponent;
